import React, { useEffect, useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import {
  Box,
  Button,
  Field,
  Flex,
  Input,
  Message,
  Modal,
  Text,
  TimeRangeInputValue,
} from '@workwhile/ui'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { ClockedTimeField } from 'global-workflows/EditShiftWorkFlow/components/ClockedTimeField'
import { useIndeedFlexShiftActionsContextValue } from '../context/IndeedFlexShiftActionsProvider'
import { SpecificWorkerIdsField } from './SpecificWorkerIdsField'

const optionalIsoDatetimeSchema = z
  .string()
  .trim()
  .optional()
  .transform((value) => (value === '' ? undefined : value))
  .refine(
    (value) => value === undefined || !Number.isNaN(Date.parse(value)),
    'Must be a valid datetime'
  )

function createIndeedFlexPastShiftFormSchema(allocationWithoutBuffer: number) {
  return z.object({
    workerIds: z
      .array(z.number().int().positive())
      .length(
        allocationWithoutBuffer,
        `Exactly ${allocationWithoutBuffer} worker ID${
          allocationWithoutBuffer === 1 ? '' : 's'
        } required`
      ),
    workers: z.array(
      z
        .object({
          workerId: z.number().int().positive(),
          clockedInAt: optionalIsoDatetimeSchema,
          clockedOutAt: optionalIsoDatetimeSchema,
          breakDurationMinutes: z.number().int().nonnegative().optional(),
        })
        .superRefine((worker, ctx) => {
          if (
            worker.clockedInAt !== undefined &&
            worker.clockedOutAt !== undefined &&
            Date.parse(worker.clockedInAt) >= Date.parse(worker.clockedOutAt)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Clock-in must be before clock-out',
              path: ['clockedOutAt'],
            })
          }
        })
    ),
  })
}

function formatDateTimeInTimeZone(
  time: string | Date | null | undefined,
  timezone: string
) {
  if (!time) {
    return ''
  }
  return formatInTimeZone(time, timezone, "yyyy-MM-dd'T'HH:mm")
}

function parseTimeRangeValueToUTCDate(
  time: TimeRangeInputValue,
  timezone: string
): Date | undefined {
  if (time instanceof Date) {
    return fromZonedTime(time, timezone)
  }
  if (typeof time !== 'string' || !time.trim()) {
    return undefined
  }

  const [datePart, timePart] = time.split('T')
  const [year, month, day] = datePart.split('-').map(Number)
  const [hours, minutes] = (timePart ?? '00:00').split(':').map(Number)
  const inputDate = new Date(year, month - 1, day, hours, minutes)

  return fromZonedTime(inputDate, timezone)
}

function parseTimeRangeValueToISOString(
  time: TimeRangeInputValue,
  timezone: string
): string | undefined {
  const utcDate = parseTimeRangeValueToUTCDate(time, timezone)
  return utcDate ? utcDate.toISOString() : undefined
}

type CreateIndeedFlexPastShiftForm = {
  workerIds: number[]
  workers: {
    workerId: number
    clockedInAt?: string
    clockedOutAt?: string
    breakDurationMinutes?: number
  }[]
}

type CreateIndeedFlexPastShiftButtonProps = {
  ifClientId: number
  ifShiftId: number
  allocationWithoutBuffer: number
  startTime: string
  endTime: string
  timezone: string
}

export const CreateIndeedFlexPastShiftButton = ({
  ifClientId,
  ifShiftId,
  allocationWithoutBuffer,
  startTime,
  endTime,
  timezone,
}: CreateIndeedFlexPastShiftButtonProps) => {
  const {
    actions: { createIndeedFlexPastShift, resetCreateIndeedFlexPastShift },
    state: { createIndeedFlexPastShiftMutationState },
  } = useIndeedFlexShiftActionsContextValue()

  const [isOpen, setIsOpen] = useState(false)

  const schema = useMemo(
    () => createIndeedFlexPastShiftFormSchema(allocationWithoutBuffer),
    [allocationWithoutBuffer]
  )

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm<CreateIndeedFlexPastShiftForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      workerIds: [],
      workers: [],
    },
  })

  const { fields } = useFieldArray({
    control,
    name: 'workers',
  })

  const workerIds = useWatch({ control, name: 'workerIds' }) ?? []
  const workerIdsKey = workerIds.join(',')

  useEffect(() => {
    const existingById = Object.fromEntries(
      getValues('workers').map((worker) => [worker.workerId, worker])
    )
    setValue(
      'workers',
      workerIds.map(
        (workerId) =>
          existingById[workerId] ?? {
            workerId,
            clockedInAt: startTime,
            clockedOutAt: endTime,
            breakDurationMinutes: undefined,
          }
      )
    )
  }, [workerIds, workerIdsKey, setValue, getValues, startTime, endTime])

  useEffect(() => {
    if (!createIndeedFlexPastShiftMutationState.isSuccess) {
      return
    }

    setIsOpen(false)
    reset()
    resetCreateIndeedFlexPastShift()
  }, [
    createIndeedFlexPastShiftMutationState.isSuccess,
    reset,
    resetCreateIndeedFlexPastShift,
  ])

  const handleClick = () => {
    resetCreateIndeedFlexPastShift()
    reset()
    setIsOpen(true)
  }

  const handleCancel = () => {
    resetCreateIndeedFlexPastShift()
    reset()
    setIsOpen(false)
  }

  const onSubmit = handleSubmit((data) => {
    createIndeedFlexPastShift({
      ifClientId,
      ifShiftId,
      workers: data.workers.map((worker) => ({
        workerId: worker.workerId,
        ...(worker.clockedInAt ? { clockedInAt: worker.clockedInAt } : {}),
        ...(worker.clockedOutAt ? { clockedOutAt: worker.clockedOutAt } : {}),
        ...(worker.breakDurationMinutes !== undefined
          ? { breakDurationMinutes: worker.breakDurationMinutes }
          : {}),
      })),
    })
  })

  return (
    <>
      <Button
        variant="secondary"
        kind="small"
        onClick={handleClick}
        style={{ padding: '0 0.5rem' }}
        disabled={createIndeedFlexPastShiftMutationState.isPending}
        loading={createIndeedFlexPastShiftMutationState.isPending}
      >
        <Text fontSize="12px">Create past shift</Text>
      </Button>
      {isOpen ? (
        <Modal open onClose={handleCancel} title="Create past WorkWhile shift">
          <Text mb={2}>
            Creates a past WorkWhile shift for Indeed Flex shift {ifShiftId} and
            assigns exactly {allocationWithoutBuffer} worker
            {allocationWithoutBuffer === 1 ? '' : 's'}. Clock times default to
            the Indeed Flex shift start/end ({timezone}).
          </Text>
          {createIndeedFlexPastShiftMutationState.isError &&
          createIndeedFlexPastShiftMutationState.errorMessage ? (
            <Message variant="error" mb={3}>
              {createIndeedFlexPastShiftMutationState.errorMessage}
            </Message>
          ) : null}
          <form onSubmit={onSubmit}>
            <SpecificWorkerIdsField
              control={control}
              name="workerIds"
              errorMessage={errors.workerIds?.message}
              label={`Assign workers (exactly ${allocationWithoutBuffer})`}
              helperText="Type a worker ID and press Enter. Workers must be active and not blocked by the company."
            />
            {fields.length > 0 ? (
              <Flex flexDirection="column" gap={3} mt={3}>
                <Text fontSize={1} fontWeight="bold">
                  Times per worker
                </Text>
                {fields.map((field, index) => (
                  <Box
                    key={field.id}
                    p={2}
                    border="1px solid"
                    borderColor="neutrals.200"
                    borderRadius="small"
                  >
                    <Text fontSize={1} fontWeight="bold" mb={2}>
                      Worker {field.workerId}
                    </Text>
                    <Flex flexDirection="column" gap={2}>
                      <Controller
                        control={control}
                        name={`workers.${index}.clockedInAt`}
                        render={({ field: inputField }) => (
                          <ClockedTimeField
                            label="Clocked In"
                            value={formatDateTimeInTimeZone(
                              inputField.value,
                              timezone
                            )}
                            error={
                              errors.workers?.[index]?.clockedInAt?.message
                            }
                            onChange={(newValue) => {
                              inputField.onChange(
                                parseTimeRangeValueToISOString(
                                  newValue,
                                  timezone
                                )
                              )
                            }}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name={`workers.${index}.clockedOutAt`}
                        render={({ field: inputField }) => (
                          <ClockedTimeField
                            label="Clocked Out"
                            value={formatDateTimeInTimeZone(
                              inputField.value,
                              timezone
                            )}
                            error={
                              errors.workers?.[index]?.clockedOutAt?.message
                            }
                            onChange={(newValue) => {
                              inputField.onChange(
                                parseTimeRangeValueToISOString(
                                  newValue,
                                  timezone
                                )
                              )
                            }}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name={`workers.${index}.breakDurationMinutes`}
                        render={({ field: inputField }) => (
                          <Field
                            label="Break duration (minutes, optional)"
                            error={
                              errors.workers?.[index]?.breakDurationMinutes
                                ?.message
                            }
                          >
                            <Input
                              type="number"
                              min={0}
                              value={
                                inputField.value === undefined
                                  ? ''
                                  : String(inputField.value)
                              }
                              onChange={(event) => {
                                const { value } = event.target
                                inputField.onChange(
                                  value === '' ? undefined : Number(value)
                                )
                              }}
                            />
                          </Field>
                        )}
                      />
                    </Flex>
                  </Box>
                ))}
              </Flex>
            ) : null}
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              gap={2}
              mt={3}
            >
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createIndeedFlexPastShiftMutationState.isPending}
                disabled={createIndeedFlexPastShiftMutationState.isPending}
              >
                Create past shift
              </Button>
            </Flex>
          </form>
        </Modal>
      ) : null}
    </>
  )
}
